import React, { FunctionComponent } from "react";
import styled, { keyframes } from "styled-components";

import * as u from "../../utils";
import * as gtag from "../../utils/gtag";
import * as T from "../Typography";
import * as I from "../Icons";
import { getUserById } from "../hooks/query/useUserQuery";
import { getMutualConnections } from "../hooks/query/useConnectionsQuery";
import { UserModel, UserSearchModel } from "../../lib/Domain/User/User";
import ImageInitials from "../ImageInitials";
import ConnectButton from "../ConnectButton";
import Link from "../Link";
import RxLoadingScreen from "../ui/display/RxLoadingScreen";
import Rx404Screen from "../ui/display/Rx404Screen";

type StyleProps = {
  isStatic?: boolean;
  isEven?: boolean;
};

type MemberProfileModalProps = {
  member: UserModel | UserSearchModel;
  isStatic?: boolean;
  onClose: () => void;
};

const MemberProfileModal: FunctionComponent<MemberProfileModalProps> = (
  props
) => {
  const thisMember = getUserById(props.member.id || props.member.objectID);
  const mutualConnections = getMutualConnections(
    props.member.id || props.member.objectID
  );
  const memberData = thisMember.data;

  const isUser = (user: UserModel | UserSearchModel): user is UserModel =>
    (user as UserModel).stripeCustomerId !== undefined;
  const interests = (memberData?.interests || []).join(" • ");
  const goals = (memberData?.goals || []).join(" • ");

  const innerRef = React.useRef(null);
  React.useEffect(attachOutsideClick, []);
  React.useEffect(attachEscKey, []);
  React.useEffect(onBlockScroll, []);

  if (thisMember.isLoading || mutualConnections.isLoading)
    return (
      <S.Container>
        <S.MemberContainer>
          <RxLoadingScreen />
        </S.MemberContainer>
      </S.Container>
    );

  if (!thisMember.data)
    return (
      <S.Container>
        <S.MemberContainer>
          <Rx404Screen />
        </S.MemberContainer>
      </S.Container>
    );

  return (
    <S.Container data-cy="member-profile">
      <S.MemberContainer ref={innerRef}>
        <S.CloseContainer onClick={onClose} data-cy={"close-profile-modal"}>
          <I.Close grow={1.3} />
        </S.CloseContainer>
        {getInfo()}
        {getBio()}
        <S.ABLogoWrap>
          <I.AllBrightVector
            colour={u.colours.shellOverlay}
            width={250}
            height={250}
          />
        </S.ABLogoWrap>
      </S.MemberContainer>
    </S.Container>
  );

  function getInfo() {
    const memberId = memberData.id || memberData.objectID;
    const imgProps = { ...memberData, size: "xxxl" };
    const jobTitle = memberData?.jobTitle || "";
    const jobCompany = memberData?.jobCompany || "";
    const occupation = [jobTitle, jobCompany].filter((a) => a).join(" • ");
    const city = (isUser(memberData) && memberData?.city) || null;
    const country = (isUser(memberData) && memberData?.country) || null;
    const location =
      memberData.location || [city, country].filter((a) => a).join(", ");

    return (
      <S.Info>
        <T.AXL href={`/member/${memberId}`}>{memberData?.name}</T.AXL>
        <ImageInitials {...imgProps} />
        <T.CL weight="500">{occupation}</T.CL>
        <S.Row>
          <I.Location />
          <T.CL weight="500">{location}</T.CL>
        </S.Row>
        {memberData.isMentor && (
          <Link href={`/mentor/${memberId}`}>
            <T.A>View mentor profile</T.A>
          </Link>
        )}
        <ConnectButton
          id={memberId}
          name={memberData.name}
          canRemoveConnection
        />
      </S.Info>
    );
  }

  function getBio() {
    const bioFields = [
      { title: "About Me", info: memberData?.bio },
      { title: "Interests", info: interests },
      { title: "Seeking", info: goals },
      { title: "Job Level", info: memberData?.jobLevel },
      { title: "Industry", info: memberData?.jobIndustry },
      {
        title: "Instagram",
        info: isUser(memberData) && memberData?.instagram,
        onClick: () => onClickLink("Instagram"),
        isLink: true,
      },
      {
        title: "Website",
        info: isUser(memberData) && memberData?.website?.toLowerCase(),
        onClick: () => onClickLink("website"),
        isLink: true,
      },
      {
        title: "LinkedIn",
        info: isUser(memberData) && memberData?.linkedin?.toLowerCase(),
        onClick: () => onClickLink("LinkedIn"),
        isLink: true,
      },
    ];

    return (
      <S.Bio>
        {bioFields
          .filter((field) => field.info)
          .map((field, index) => (
            <Bio {...field} index={index} key={field.title} />
          ))}
        <S.BioRow />
      </S.Bio>
    );

    function onClickLink(platform) {
      gtag.event({
        name: gtag.GA_EVENT.SOCIAL_ENGAGEMENT,
        category: "Engagement",
        label: "Click Member Profile Link",
        value: platform,
      });
    }
  }

  function onBlockScroll() {
    document.body.setAttribute("style", "overflow: hidden;");
    return () => document.body.setAttribute("style", "overflow: visible;");
  }

  function attachOutsideClick() {
    document.addEventListener("click", onMaybeOutside);
    return () => document.removeEventListener("click", onMaybeOutside);

    function onMaybeOutside(evt) {
      if (innerRef.current?.contains(evt.target)) return;
      onClose();
    }
  }

  function attachEscKey() {
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);

    function onKeyDown(evt) {
      if (evt.key === "Escape") onClose();
      return;
    }
  }

  function onClose() {
    if (props.onClose) props.onClose();
  }
};

function Bio(props) {
  if (!props.info) return null;

  return (
    <S.BioRow>
      <S.RowColour isEven={props.index % 2 == 0} />
      <S.RowText>
        <T.H6>{props.title}</T.H6>
        {props.isLink ? (
          <Link href={getMaybeLink()}>
            <T.CL weight="500">
              <u>{props.info}</u>
            </T.CL>
          </Link>
        ) : (
          <T.CL allowWhiteSpace>{props.info}</T.CL>
        )}
      </S.RowText>
    </S.BioRow>
  );

  function getMaybeLink() {
    if (props?.info?.startsWith("http")) return props?.info;
    if (props?.title === "Instagram")
      return [
        "https://www.instagram.com/",
        props?.info?.split("@")[1] || props?.info,
      ].join("");

    return `http://${props?.info}`;
  }
}

const S = () => {};

S.Fade = keyframes`
  100% {opacity: 1}
`;

S.Container = styled.div`
  animation: ${S.Fade} 0.2s ease-out forwards;
  z-index: 99;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: hsla(0, 0%, 0%, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  overflow: auto;
`;

S.MemberContainer = styled.div`
  background: ${u.colours.lightShell};
  position: relative;
  margin-top: 20px;
  border-radius: 5px;
  box-shadow: 0 0 15px ${u.colours.charcoalFaded};
  width: 90%;
  height: 90%;

  @media ${u.device.mobile} {
    max-width: 900px;
    max-height: 500px;
    display: flex;
  }
`;

S.CloseContainer = styled.div`
  position: absolute;
  z-index: 10;
  border-radius: 50%;
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: center;
  height: 50px;
  width: 50px;
  transition: transform 0.1s ease-out;

  &:hover {
    transform: scale(1.1);
  }
`;

S.ABLogoWrap = styled.div`
  position: absolute;
  opacity: 0.6;
  top: 0;
  right: 0;
  left: 0;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  @media ${u.device.mobile} {
    right: 20px;
    top: 10px;
    left: auto;
  }
`;

S.Info = styled.div`
  position: relative;
  padding: 36px 20px;
  z-index: 2;
  display: flex;
  flex: 2;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: space-evenly;
  gap: 15px;

  @media ${u.device.mobile} {
    height: 100%;
  }
`;

S.Bio = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 3;
  padding: 0;
  overflow-y: auto;

  @media ${u.device.mobile} {
    padding: 60px 0 0 0;
  }
`;

S.BioRow = styled.div`
  position: relative;
  padding: 10px 40px;
  overflow-wrap: break-word;
`;

S.RowColour = styled.div<StyleProps>`
  position: absolute;
  height: 100%;
  width: 100%;
  background: ${(p) => (p.isEven ? u.colours.lightShell : u.colours.shell)};
  left: 0;
  top: 0;
`;

S.RowText = styled.div`
  position: relative;
  z-index: 2;
`;

S.Row = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export default MemberProfileModal;
